/**
 * `/app/widgets/add/:widgetId` — provisions a published daily brief into the
 * visitor's own account.
 *
 * The public widget pages are static HTML with zero JavaScript, so their CTA
 * is a plain link and every decision happens here. Two things this route does
 * NOT do, both deliberate:
 *
 * - It does not call a create endpoint. There isn't one, by design: workflow
 *   interactions are agent tool calls, and `infra/docs/api-spec.md` §3 records
 *   that the former `POST /workflows` was removed. The workflow is created by
 *   Vera answering a turn with `workflow_create` — which also means the tool
 *   call renders in the transcript, per AGENTS.md's show-don't-hide principle.
 * - It does not create a schedule. That would collide with
 *   `ACTIVE_SCHEDULE_LIMIT = 2` in the middle of an acquisition funnel; the
 *   user enables it later through the existing UI.
 *
 * Living under `/app` is load-bearing: `auth/postAuthRedirect.ts` constrains
 * post-auth targets to `^\/(agent|app)(\/|$)`, so a signed-out visitor bounced
 * through sign-up returns here. A route outside that allowlist would be
 * silently dropped to `/app` and the visitor would lose the brief they asked
 * for.
 */
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { createWorkflowMessage, widgetById } from '../../widgets/catalog.ts';
import './AddWidget.css';

/** The agent that owns scheduled workflows (single-agent v1). */
const WORKFLOW_AGENT = 'vera';

export default function AddWidget() {
  const { widgetId } = useParams<{ widgetId: string }>();
  const navigate = useNavigate();
  const widget = widgetId === undefined ? undefined : widgetById(widgetId);

  useEffect(() => {
    if (widget === undefined) {
      return;
    }
    // Hand the console the turn to send. It fires once and strips the router
    // state, so a refresh or a back-navigation never re-creates the workflow.
    navigate(`/agent/${WORKFLOW_AGENT}`, {
      replace: true,
      state: { autoMessage: createWorkflowMessage(widget) },
    });
  }, [widget, navigate]);

  if (widget === undefined) {
    return (
      <main className="om-addw">
        <h1 className="om-addw__title">We don&rsquo;t recognise that brief</h1>
        <p className="om-addw__body">
          The link may be out of date, or the brief may have been retired.
        </p>
        <Link className="om-addw__cta" to="/widgets">
          See today&rsquo;s briefs
        </Link>
      </main>
    );
  }

  return (
    <main className="om-addw">
      <h1 className="om-addw__title">Adding {widget.title}&hellip;</h1>
      <p className="om-addw__body">Handing this to {WORKFLOW_AGENT.toUpperCase()} to set up.</p>
    </main>
  );
}
